import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseMap, { pinIcon } from './BaseMap';
import { CategoryTag, SectionCard, StatusPill } from '../common/ui';
import JobService from '../../services/JobService';
import { useToast } from '../../contexts/ToastContext';
import { CRS, CRS_ORDER, emapaUrl, fmtDistance, fromLatLng, navigationUrl } from '../../utils/geo';
import { fmtDate } from '../../utils/format';

const RADII = [200, 500, 1000, 2000];

/** The job's pin on a map that fills the screen (view only). */
function BigMapDialog({ job, open, onClose }) {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, pt: 'calc(10px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #E2E8F0' }}>
                    <IconButton onClick={onClose} aria-label={t('common.close')}><CloseIcon /></IconButton>
                    <Typography noWrap sx={{ flex: 1, fontWeight: 700, fontSize: 16, color: '#1a2332' }}>{t('location.title')} · {job.title}</Typography>
                    <Button variant="outlined" endIcon={<OpenInNewIcon />} href={emapaUrl(job.latitude, job.longitude)} target="_blank" rel="noopener noreferrer"
                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                        {t('location.openEmapa')}
                    </Button>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {open && (
                        <BaseMap height="100%" center={[job.latitude, job.longitude]} zoom={18} sx={{ borderRadius: 0, border: 0 }}
                                 onReady={(map) => L.marker([job.latitude, job.longitude], { icon: pinIcon('#EF4444', 24) }).addTo(map)} />
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}

/**
 * Location section of the job page: map preview, coordinates, eMapa link and nearby jobs.
 * Without `onEdit` (employees) it is view-only and leaves out nearby jobs, which are other people's work.
 */
export default function JobLocationCard({ job, onEdit }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [radius, setRadius] = useState(500);
    const [nearby, setNearby] = useState(null);
    const [big, setBig] = useState(false);
    const mapRef = useRef(null);
    const has = job.latitude != null;

    useEffect(() => {
        if (!has || !onEdit) { setNearby(null); return; }
        JobService.nearby(job.id, radius).then(setNearby).catch(() => setNearby([]));
    }, [job.id, job.latitude, job.longitude, radius, has, onEdit]);

    // Keep the preview in sync when the location is edited.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !has) return;
        map.eachLayer(l => { if (l instanceof L.Marker) l.remove(); });
        L.marker([job.latitude, job.longitude], { icon: pinIcon('#EF4444', 22) }).addTo(map);
        map.setView([job.latitude, job.longitude], 17);
    }, [job.latitude, job.longitude, has]);

    if (!has) {
        return (
            <SectionCard title={t('location.title')}>
                <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 13.5, color: '#94A3B8', mb: 1.5 }}>{t('location.none')}</Typography>
                    {onEdit && <Button variant="outlined" startIcon={<AddLocationAltOutlinedIcon />} onClick={onEdit}>{t('location.add')}</Button>}
                </Box>
            </SectionCard>
        );
    }

    const copy = (text) => navigator.clipboard?.writeText(text).then(() => showToast(t('location.copied'), 'success')).catch(() => {});

    return (
        <SectionCard title={t('location.title')} action={onEdit ? <Button size="small" onClick={onEdit}>{t('location.change')}</Button> : null}>
            <Box sx={{ p: 2 }}>
                <Box sx={{ position: 'relative' }}>
                    <BaseMap height={220} scrollWheelZoom={false} center={[job.latitude, job.longitude]} zoom={17}
                             onReady={(map) => { mapRef.current = map; }} />
                    <Tooltip title={t('location.enlarge')} placement="right">
                        <IconButton onClick={() => setBig(true)} aria-label={t('location.enlarge')} size="small" sx={{
                            position: 'absolute', left: 10, bottom: 10, zIndex: 1000, bgcolor: '#fff', border: '2px solid rgba(0,0,0,0.2)',
                            borderRadius: '4px', '&:hover': { bgcolor: '#F4F4F4' },
                        }}><OpenInFullIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                </Box>
                <BigMapDialog job={job} open={big} onClose={() => setBig(false)} />

                <Box sx={{ mt: 1.5 }}>
                    {CRS_ORDER.map(k => {
                        const [x, y] = fromLatLng(k, job.latitude, job.longitude);
                        const sys = CRS[k];
                        const text = `${x}, ${y}`;
                        return (
                            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: '1px solid #F5F7FA', '&:last-of-type': { borderBottom: 0 } }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t(`location.crsShort.${k}`)}</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, fontSize: 12.5, color: '#1E293B', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                        <span>{t(`location.axis.${sys.first}`)} {x}</span>
                                        <span>{t(`location.axis.${sys.second}`)} {y}</span>
                                    </Box>
                                </Box>
                                <Tooltip title={t('location.copy')}><IconButton size="small" onClick={() => copy(text)}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                            </Box>
                        );
                    })}
                </Box>

                <Button fullWidth variant="contained" startIcon={<DirectionsCarOutlinedIcon />} href={navigationUrl(job.latitude, job.longitude)}
                        target="_blank" rel="noopener noreferrer" sx={{ mt: 1.5 }}>
                    {t('location.navigate')}
                </Button>
                <Button fullWidth variant="contained" endIcon={<OpenInNewIcon />} href={emapaUrl(job.latitude, job.longitude)}
                        target="_blank" rel="noopener noreferrer" sx={{ mt: 1, bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' } }}>
                    {t('location.openEmapa')}
                </Button>
                <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 0.75, textAlign: 'center' }}>{t('location.emapaHint')}</Typography>
            </Box>

            {onEdit && <Box sx={{ borderTop: '1px solid #F1F5F9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.25 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a2332' }}>{t('location.nearbyTitle')}</Typography>
                    <TextField select size="small" value={radius} onChange={(e) => setRadius(Number(e.target.value))} sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: 12.5 } }}>
                        {RADII.map(r => <MenuItem key={r} value={r}>{t('location.within', { distance: fmtDistance(r) })}</MenuItem>)}
                    </TextField>
                </Box>
                {nearby === null ? null : nearby.length === 0 ? (
                    <Typography sx={{ px: 2.5, pb: 2, fontSize: 12.5, color: '#94A3B8' }}>{t('location.nearbyNone')}</Typography>
                ) : nearby.map(n => (
                    <Box key={n.id} onClick={() => navigate(`/jobs/${n.id}`)} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1, cursor: 'pointer',
                        borderTop: '1px solid #F5F7FA', '&:hover': { bgcolor: '#F8FAFC' },
                    }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#427898', width: 56, flexShrink: 0 }}>{fmtDistance(n.distanceMeters)}</Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{n.title}</Typography>
                            <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>{[n.number, n.clientName, fmtDate(n.createdAt)].filter(Boolean).join(' · ')}</Typography>
                        </Box>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}><CategoryTag category={n.category} /></Box>
                        <StatusPill status={n.status} size="sm" />
                    </Box>
                ))}
            </Box>}
        </SectionCard>
    );
}
